import { normalizeId, normalizeRoleKey } from "./roles-permissions";

const SYSTEM_ROLE_LABELS = {
  system_admin: "System Admin",
  business_unit: "Business Unit",
  super_admin: "Super Admin",
  reseller: "Reseller",
  solution_provider: "Solution Provider",
  bank_admin: "Bank Admin",
  sme_user: "SME User",
};

const SYSTEM_ROLE_OPTIONS = [
  { value: "system_admin", label: "System Admin" },
  { value: "business_unit", label: "Business Unit" },
  { value: "super_admin", label: "Super Admin" },
  { value: "reseller", label: "Reseller" },
  { value: "solution_provider", label: "Solution Provider" },
  { value: "bank_admin", label: "Bank Admin" },
  { value: "sme_user", label: "SME User" },
];

const resolveRoleLabel = (role, t, fallback, customRoleName = "") => {
  if (!role) return "";
  const normalizedRole = normalizeRoleKey(role);
  const trimmedCustomRoleName =
    typeof customRoleName === "string" ? customRoleName.trim() : "";
  if (normalizedRole === "super_admin" && trimmedCustomRoleName) {
    return trimmedCustomRoleName;
  }
  if (typeof t === "function") {
    const roleTranslation = t(`common.roles.${normalizedRole}`, {
      defaultValue: "",
    });
    if (roleTranslation) return roleTranslation;
  }
  const label = SYSTEM_ROLE_LABELS[normalizedRole];
  return label && typeof t === "function"
    ? t(`userManagement.roles.${normalizedRole}`, label)
    : fallback || role;
};

const shouldHideRoleOption = (value, currentRoleKey) => {
  const role = normalizeRoleKey(value);
  const currentRole = normalizeRoleKey(currentRoleKey);
  if (!role) return true;
  if (currentRole === "business_unit") {
    return role === "business_unit" || role === "system_admin";
  }
  if (currentRole === "system_admin") {
    return role === "system_admin";
  }
  return false;
};

const resolveRoleCompanyId = (role = {}) =>
  role.companyId ||
  role.company?.id ||
  role.company?._id ||
  role.company?.companyId ||
  role.companyDetails?.id ||
  role.companyDetails?._id ||
  role.companyDetails?.companyId ||
  "";

const buildRoleOptions = ({
  roles = [],
  companyId,
  currentRoleKey,
  selectedRole,
  t,
}) => {
  const normalizedCompanyId = normalizeId(companyId);
  const filteredRoles = roles.filter((role) => {
    if (!normalizedCompanyId) return true;
    return normalizeId(resolveRoleCompanyId(role)) === normalizedCompanyId;
  });

  let options = filteredRoles
    .map((role) => {
      const value = role.name || role.key || role.value || role._id || role.id;
      if (!value) return null;
      if (shouldHideRoleOption(value, currentRoleKey)) return null;
      const fallbackLabel = role.title || role.name || role.key || value;
      return {
        value: String(value),
        label: resolveRoleLabel(value, t, fallbackLabel),
      };
    })
    .filter(Boolean);

  if (!options.length) {
    options = SYSTEM_ROLE_OPTIONS.filter(
      (role) => !shouldHideRoleOption(role.value, currentRoleKey),
    ).map((role) => ({
      value: role.value,
      label: t(`userManagement.roles.${role.value}`, role.label),
    }));
  }

  if (
    selectedRole &&
    !options.some((option) => option.value === selectedRole)
  ) {
    options = [
      ...options,
      {
        value: selectedRole,
        label: resolveRoleLabel(selectedRole, t, selectedRole),
        hidden: shouldHideRoleOption(selectedRole, currentRoleKey),
      },
    ];
  }

  return options;
};

const resolveRoleGroup = (roleValue, roles = []) => {
  const normalized = normalizeRoleKey(roleValue);
  if (!normalized) return "";
  const match = roles.find((role) => {
    const roleName = normalizeRoleKey(
      role?.name || role?.title || role?.key || role?.role || role?.value || "",
    );
    return roleName === normalized;
  });
  return normalizeRoleKey(match?.group || roleValue);
};

const resolveModuleAudience = ({ roleValue, roles, isCompanyAdmin }) => {
  if (!isCompanyAdmin) return "user";
  const audience = resolveRoleGroup(roleValue, roles);
  if (!audience || audience === "user") return "user";
  return audience;
};

export {
  SYSTEM_ROLE_LABELS,
  SYSTEM_ROLE_OPTIONS,
  resolveRoleLabel,
  shouldHideRoleOption,
  buildRoleOptions,
  resolveRoleGroup,
  resolveModuleAudience,
};
